import React from 'react';

interface Player {
  id: string;
  name: string;
  score: number;
  avatar?: string;
}

interface MiniLeaderboardProps {
  players: Player[];
  myPlayerId?: string;
  answeredPlayerIds?: Set<string>;
  maxPlayers?: number;
  showScores?: boolean;
}

// CSS for responsive grid
const responsiveStyles = `
  .mini-leaderboard-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    width: 100%;
  }
  
  @media (min-width: 768px) {
    .mini-leaderboard-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  
  @media (min-width: 1024px) {
    .mini-leaderboard-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }
`;

const MiniLeaderboard: React.FC<MiniLeaderboardProps> = ({
  players,
  myPlayerId,
  answeredPlayerIds = new Set(),
  maxPlayers = 8,
  showScores = true,
}) => {
  const sortedPlayers = [...players].sort((a, b) => b.score - a.score).slice(0, maxPlayers);

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={{
        width: '100%',
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        borderRadius: '10px',
        padding: '0.8rem',
        marginBottom: '1rem',
      }}>
        <div className="mini-leaderboard-grid">
          {sortedPlayers.map((player, index) => {
            const isMe = player.id === myPlayerId;
            const hasAnswered = answeredPlayerIds.has(player.id);
            const isAvatarImage = player.avatar && (
              player.avatar.startsWith('/') || 
              player.avatar.startsWith('data:') || 
              player.avatar.includes('.svg') ||
              player.avatar.includes('media')
            );

            return (
              <div 
                key={player.id} 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.4rem',
                  padding: '0.5rem 0.8rem',
                  backgroundColor: isMe ? 'rgba(var(--color-primary-rgb), 0.2)' : 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '8px',
                  border: isMe ? '2px solid var(--color-primary)' : '1px solid rgba(255,255,255,0.1)',
                  minWidth: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                    {index > 2 && (
                      isAvatarImage
                        ? <img src={player.avatar} alt="" style={{ width: '1.2rem', height: '1.2rem', objectFit: 'contain' }} />
                        : <span style={{ fontSize: '1rem' }}>{player.avatar || '👤'}</span>
                    )}
                  </span>
                  <span style={{ 
                    color: isMe ? 'var(--color-primary)' : '#fff', 
                    fontSize: '0.85rem',
                    fontWeight: isMe ? 'bold' : 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}>
                    {player.name}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                  {showScores && (
                    <span style={{ 
                      color: index === 0 ? '#FACC15' : 'rgba(255,255,255,0.7)', 
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                    }}>
                      {player.score}
                    </span>
                  )}
                  {hasAnswered && (
                    <span style={{ color: 'var(--color-primary)', fontSize: '0.9rem' }}>✓</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default MiniLeaderboard;

